import { useMemo, useState } from 'react';
import type { CalendarDate, CalendarDateTime, Time } from '@internationalized/date';
import { Card, Description, Label, TimeField } from '@heroui/react';
import { Clock } from 'lucide-react';
import type { AvailabilityWindow } from '../../api/my-parking-spots';
import {
  clampDateTimeToWindowBounds,
  getAllowedEndBoundsOnDate,
  getEarliestAllowedStartOnDate,
  hasAnyAvailabilityWindowOnDate,
  isDateFullyBlockedByWindows,
  isEndDateFullyBlockedByWindows,
} from '../../features/my-parking-spots/utils/availability-windows';
import {
  calendarDateTimeToDate,
  calendarDateTimeToIsoUtc,
  calendarDateTimeToTime,
  formatDateTimeBR,
  mergeCalendarDateTime,
} from '../../utils/date-utils';
import { HeroUiCalendarGrid } from './hero-ui-calendar-grid';

interface InlineDateTimeSectionProps {
  title: string;
  value: CalendarDateTime | null;
  onChange: (value: CalendarDateTime) => void;
  minValue?: CalendarDateTime;
  maxValue?: CalendarDateTime;
  /** Janelas ativas para marcar dias no calendário. */
  markedWindows?: AvailabilityWindow[];
  /** Ao editar, ignora a janela em edição nas restrições de horário. */
  unavailableExcludeWindowId?: string;
  fieldMode?: 'start' | 'end';
  /** Obrigatório quando `fieldMode="end"`. */
  startAtReference?: CalendarDateTime | null;
}

function buildTimeConstraintMessage(
  mode: 'start' | 'end',
  clampedValue: CalendarDateTime,
  reason: 'before-window-end' | 'before-min' | 'after-max',
): string {
  const formatted = formatDateTimeBR(calendarDateTimeToIsoUtc(clampedValue));

  if (mode === 'start' && reason === 'before-window-end') {
    return `Horário ajustado para ${formatted} — há janela ativa antes desse horário.`;
  }

  if (mode === 'end' && reason === 'after-max') {
    return `Horário ajustado para ${formatted} — há janela ativa depois desse horário.`;
  }

  return `Horário ajustado para ${formatted}.`;
}

export function InlineDateTimeSection({
  title,
  value,
  onChange,
  minValue,
  maxValue,
  markedWindows = [],
  unavailableExcludeWindowId,
  fieldMode = 'start',
  startAtReference = null,
}: InlineDateTimeSectionProps) {
  const [timeConstraintMessage, setTimeConstraintMessage] = useState<string | null>(null);

  const earliestAllowedOnSelectedDate = useMemo(() => {
    if (!value || markedWindows.length === 0) return null;

    if (fieldMode === 'start') {
      return getEarliestAllowedStartOnDate(
        calendarDateTimeToDate(value),
        markedWindows,
        unavailableExcludeWindowId,
        minValue,
      );
    }

    if (!startAtReference) return null;
    return getAllowedEndBoundsOnDate(
      calendarDateTimeToDate(value),
      startAtReference,
      markedWindows,
      unavailableExcludeWindowId,
      minValue,
    );
  }, [
    fieldMode,
    markedWindows,
    minValue,
    startAtReference,
    unavailableExcludeWindowId,
    value,
  ]);

  if (!value) return null;

  const dateValue = calendarDateTimeToDate(value);
  const timeValue = calendarDateTimeToTime(value);

  const clampAndNotify = (next: CalendarDateTime) => {
    const clamped = clampDateTimeToWindowBounds(next, fieldMode, markedWindows, {
      excludeWindowId: unavailableExcludeWindowId,
      absoluteMin: minValue,
      startAtReference,
    });

    if (clamped.wasClamped && clamped.reason) {
      setTimeConstraintMessage(
        buildTimeConstraintMessage(fieldMode, clamped.value, clamped.reason),
      );
    } else {
      setTimeConstraintMessage(null);
    }

    onChange(clamped.value);
  };

  const handleDateChange = (date: CalendarDate) => {
    clampAndNotify(mergeCalendarDateTime(date, calendarDateTimeToTime(value)));
  };

  const handleTimeChange = (time: Time | null) => {
    if (!time) return;
    clampAndNotify(mergeCalendarDateTime(calendarDateTimeToDate(value), time));
  };

  return (
    <Card variant="secondary">
      <Card.Header className="border-b border-separator pb-3">
        <Card.Title className="text-base font-semibold">{title}</Card.Title>
      </Card.Header>
      <Card.Content className="flex w-full flex-col gap-2 pt-4">
        <div className="flex justify-center">
          <HeroUiCalendarGrid
            ariaLabel={title}
            value={dateValue}
            onChange={handleDateChange}
            minValue={minValue ? calendarDateTimeToDate(minValue) : undefined}
            maxValue={maxValue ? calendarDateTimeToDate(maxValue) : undefined}
            hasCellIndicator={(date) => hasAnyAvailabilityWindowOnDate(date, markedWindows)}
            isDateUnavailable={(date) =>
              fieldMode === 'end' && startAtReference
                ? isEndDateFullyBlockedByWindows(
                    date,
                    startAtReference,
                    markedWindows,
                    unavailableExcludeWindowId,
                    minValue,
                  )
                : isDateFullyBlockedByWindows(
                    date,
                    markedWindows,
                    unavailableExcludeWindowId,
                    minValue,
                  )
            }
          />
        </div>

        <div className="flex flex-col items-center gap-1.5 border-t border-separator pt-2">
          <Label className="text-sm font-medium">Hora</Label>
          <TimeField
            aria-label={`Hora — ${title}`}
            granularity="minute"
            hideTimeZone
            hourCycle={24}
            value={timeValue}
            onChange={handleTimeChange}
          >
            <TimeField.Group>
              <TimeField.Prefix>
                <Clock aria-hidden className="size-4 shrink-0 text-muted" />
              </TimeField.Prefix>
              <TimeField.Input>
                {(segment) => <TimeField.Segment segment={segment} />}
              </TimeField.Input>
            </TimeField.Group>
          </TimeField>
          {earliestAllowedOnSelectedDate &&
          hasAnyAvailabilityWindowOnDate(dateValue, markedWindows) ? (
            <Description className="text-center text-xs">
              {fieldMode === 'start' ? (
                <>
                  A partir de{' '}
                  {formatDateTimeBR(calendarDateTimeToIsoUtc(earliestAllowedOnSelectedDate))} neste
                  dia.
                </>
              ) : (
                <>
                  Entre{' '}
                  {formatDateTimeBR(
                    calendarDateTimeToIsoUtc(earliestAllowedOnSelectedDate.min),
                  )}{' '}
                  e{' '}
                  {formatDateTimeBR(
                    calendarDateTimeToIsoUtc(earliestAllowedOnSelectedDate.max),
                  )}{' '}
                  neste dia.
                </>
              )}
            </Description>
          ) : null}
          {timeConstraintMessage ? (
            <Description className="text-center text-xs text-warning">{timeConstraintMessage}</Description>
          ) : null}
        </div>
      </Card.Content>
    </Card>
  );
}
